import React, { useState, useEffect } from 'react';
import { useAuth, axios } from '../../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

/* ══════════════════════════════════════════════════════════════
   CSS KEYFRAMES — inject once into <head>
══════════════════════════════════════════════════════════════ */
const injectKeyframes = () => {
  if (document.getElementById('dp-runner-kf')) return;
  const style = document.createElement('style');
  style.id = 'dp-runner-kf';
  style.textContent = `
    @keyframes dp-walk-body  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
    @keyframes dp-jog-body   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
    @keyframes dp-sprint-body{ 0%,100%{transform:translateY(0)} 25%,75%{transform:translateY(-6px)} }

    @keyframes dp-leg-l-walk  { 0%,100%{transform:rotate(-18deg)} 50%{transform:rotate(18deg)} }
    @keyframes dp-leg-r-walk  { 0%,100%{transform:rotate(18deg)}  50%{transform:rotate(-18deg)} }
    @keyframes dp-leg-l-jog   { 0%,100%{transform:rotate(-32deg)} 50%{transform:rotate(32deg)} }
    @keyframes dp-leg-r-jog   { 0%,100%{transform:rotate(32deg)}  50%{transform:rotate(-32deg)} }
    @keyframes dp-leg-l-sprint{ 0%,100%{transform:rotate(-48deg)} 50%{transform:rotate(48deg)} }
    @keyframes dp-leg-r-sprint{ 0%,100%{transform:rotate(48deg)}  50%{transform:rotate(-48deg)} }

    @keyframes dp-arm-l-walk  { 0%,100%{transform:rotate(-15deg)} 50%{transform:rotate(15deg)} }
    @keyframes dp-arm-r-walk  { 0%,100%{transform:rotate(15deg)}  50%{transform:rotate(-15deg)} }
    @keyframes dp-arm-l-sprint{ 0%,100%{transform:rotate(-55deg)} 50%{transform:rotate(35deg)} }
    @keyframes dp-arm-r-sprint{ 0%,100%{transform:rotate(55deg)}  50%{transform:rotate(-35deg)} }

    @keyframes dp-speed-line  { 0%{transform:translateX(0);opacity:.7} 100%{transform:translateX(-28px);opacity:0} }
    @keyframes dp-dust        { 0%{transform:scale(0) translateY(0);opacity:.5} 100%{transform:scale(2.5) translateY(-14px);opacity:0} }
    @keyframes dp-sweat       { 0%{transform:translateY(0) scale(1);opacity:.9} 100%{transform:translateY(-8px) scale(0.5);opacity:0} }

    @keyframes dp-jump        { 0%,100%{transform:translateY(0) rotate(0deg)} 35%{transform:translateY(-22px) rotate(-8deg)} 65%{transform:translateY(-10px) rotate(6deg)} }
    @keyframes dp-confetti-0  { 0%{transform:translateY(0) rotate(0deg);  opacity:1} 100%{transform:translateY(44px) rotate(380deg); opacity:0} }
    @keyframes dp-confetti-1  { 0%{transform:translateY(0) rotate(0deg);  opacity:1} 100%{transform:translateY(38px) rotate(-290deg);opacity:0} }
    @keyframes dp-confetti-2  { 0%{transform:translateY(0) rotate(0deg);  opacity:1} 100%{transform:translateY(50px) rotate(440deg); opacity:0} }
    @keyframes dp-confetti-3  { 0%{transform:translateY(0) rotate(0deg);  opacity:1} 100%{transform:translateY(42px) rotate(-320deg);opacity:0} }
    @keyframes dp-confetti-4  { 0%{transform:translateY(0) rotate(0deg);  opacity:1} 100%{transform:translateY(36px) rotate(260deg); opacity:0} }
    @keyframes dp-trophy-glow { 0%,100%{filter:drop-shadow(0 0 4px #E8334A);transform:scale(1) rotate(-3deg)} 50%{filter:drop-shadow(0 0 22px #E8334A) drop-shadow(0 0 40px #FF455080);transform:scale(1.18) rotate(3deg)} }
    @keyframes dp-star-spin   { from{transform:rotate(0deg) translateX(24px) rotate(0deg)} to{transform:rotate(360deg) translateX(24px) rotate(-360deg)} }

    @keyframes dp-flag-wave   { 0%,100%{skewX(0)} 33%{transform:skewX(-7deg)} 66%{transform:skewX(7deg)} }
    @keyframes dp-track-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,0)} 50%{box-shadow:0 0 0 4px rgba(37,99,235,0.15)} }
    @keyframes dp-runner-move { 0%{left:2%} 100%{left:calc(var(--rp,50%) - 36px)} }

    @keyframes dp-fire-0 { 0%{transform:scale(0.9) rotate(-8deg) translateY(0);opacity:.85} 50%{transform:scale(1.4) rotate(8deg) translateY(-4px);opacity:1} 100%{transform:scale(0.9) rotate(-8deg) translateY(0);opacity:.85} }
    @keyframes dp-fire-1 { 0%{transform:scale(0.7) rotate(10deg) translateY(0);opacity:.6} 50%{transform:scale(1.2) rotate(-6deg) translateY(-3px);opacity:.95} 100%{transform:scale(0.7) rotate(10deg) translateY(0);opacity:.6} }
    @keyframes dp-sprint-flash { 0%,100%{opacity:1} 50%{opacity:0.7} }
    @keyframes dp-bg-pulse-red  { 0%,100%{background:rgba(232,51,74,0.04)} 50%{background:rgba(232,51,74,0.12)} }
    @keyframes dp-bg-pulse-blue { 0%,100%{background:rgba(37,99,235,0.04)} 50%{background:rgba(37,99,235,0.1)} }
    @keyframes dp-pill-bounce   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
    @keyframes dp-progress-glow { 0%,100%{box-shadow:0 0 8px rgba(220,38,38,0.4)} 50%{box-shadow:0 0 20px rgba(220,38,38,0.8)} }
  `;
  document.head.appendChild(style);
};

/* ══════════════════════════════════════════════════════════════
   RUNNER CHARACTER
   phase: 'walk' | 'jog' | 'sprint' | 'celebrate'
══════════════════════════════════════════════════════════════ */
function RunnerChar({ phase }) {
  useEffect(() => { injectKeyframes(); }, []);

  const cfg = {
    walk:      { bodySpd:'1.2s', legSpd:'0.8s', armSpd:'0.9s', shirt:'#BFDBFE', pants:'#1E40AF', shoes:'#1E3A8A', skin:'#FBBF24', hair:'#1E293B' },
    jog:       { bodySpd:'0.7s', legSpd:'0.5s', armSpd:'0.65s',shirt:'#3B82F6', pants:'#1E3A5F', shoes:'#1E3A5F', skin:'#FBBF24', hair:'#1E293B' },
    sprint:    { bodySpd:'0.3s', legSpd:'0.22s',armSpd:'0.25s',shirt:'#E8334A', pants:'#7F1D1D', shoes:'#991B1B', skin:'#FBBF24', hair:'#1E293B' },
    celebrate: { bodySpd:'0.5s', legSpd:'0.4s', armSpd:'0.45s',shirt:'#E8334A', pants:'#7F1D1D', shoes:'#991B1B', skin:'#FBBF24', hair:'#1E293B' },
  }[phase] || {};

  const bodyAnim = { walk:'dp-walk-body', jog:'dp-jog-body', sprint:'dp-sprint-body', celebrate:'dp-jump' }[phase];
  const legTypeL = phase === 'sprint' ? 'dp-leg-l-sprint' : phase === 'jog' ? 'dp-leg-l-jog' : 'dp-leg-l-walk';
  const legTypeR = phase === 'sprint' ? 'dp-leg-r-sprint' : phase === 'jog' ? 'dp-leg-r-jog' : 'dp-leg-r-walk';
  const armL     = phase === 'sprint' ? 'dp-arm-l-sprint' : 'dp-arm-l-walk';
  const armR     = phase === 'sprint' ? 'dp-arm-r-sprint' : 'dp-arm-r-walk';

  if (phase === 'celebrate') {
    return (
      <div style={{ position:'relative', width:68, height:92 }}>
        {['#E8334A','#2563EB','#DC2626','#1D4ED8','#FF4560'].map((c,i) => (
          <div key={i} style={{
            position:'absolute', top:0, left: 6 + i*13, width:7, height:7,
            background:c, borderRadius: i%2===0 ? '50%' : '2px',
            animation:`dp-confetti-${i} ${0.75+i*0.12}s ease-in ${i*0.09}s infinite`,
          }}/>
        ))}
        <div style={{
          position:'absolute', top:4, left:'50%', transform:'translateX(-50%)',
          fontSize:'1.8rem', zIndex:2, animation:'dp-trophy-glow 1.4s ease-in-out infinite',
        }}>🏆</div>
        {[0,1,2].map(i => (
          <div key={i} style={{
            position:'absolute', top:'42%', left:'50%', width:10, height:10, marginTop:-5, marginLeft:-5,
            animation:`dp-star-spin ${1+i*0.35}s linear ${i*0.18}s infinite`, fontSize:'0.7rem',
          }}>{['⭐','💥','🌟'][i]}</div>
        ))}
        <div style={{
          position:'absolute', bottom:0, left:'50%', transform:'translateX(-50%)',
          animation:`dp-jump 0.55s ease-in-out infinite`,
        }}>
          <div style={{ width:22, height:22, borderRadius:'50%', background:cfg.skin, margin:'0 auto 2px', border:`2px solid #D97706`, position:'relative' }}>
            <div style={{ position:'absolute', top:6, left:4,  width:4, height:4, borderRadius:'50%', background:'#1E293B' }}/>
            <div style={{ position:'absolute', top:6, right:4, width:4, height:4, borderRadius:'50%', background:'#1E293B' }}/>
            <div style={{ position:'absolute', bottom:4, left:'50%', transform:'translateX(-50%)', width:8, height:4, borderRadius:'0 0 4px 4px', background:'#E8334A' }}/>
          </div>
          <div style={{ width:26, height:22, background:cfg.shirt, borderRadius:'6px 6px 3px 3px', margin:'0 auto', position:'relative', border:`2px solid ${cfg.shirt}` }}>
            <div style={{ position:'absolute', top:1, left:-11, width:11, height:5, background:cfg.skin, borderRadius:4, transform:'rotate(-55deg)', transformOrigin:'right center' }}/>
            <div style={{ position:'absolute', top:1, right:-11, width:11, height:5, background:cfg.skin, borderRadius:4, transform:'rotate(55deg)', transformOrigin:'left center' }}/>
          </div>
          <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
            {[0,1].map(i => (
              <div key={i}>
                <div style={{ width:7, height:14, background:cfg.pants, borderRadius:'4px 4px 0 0' }}/>
                <div style={{ width:11, height:5, background:cfg.shoes, borderRadius:'0 5px 5px 2px', marginLeft:1 }}/>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:'relative', width:54, height:82 }}>
      {phase === 'sprint' && [0,1,2].map(i => (
        <div key={i} style={{
          position:'absolute', left:-18, top:26+i*11, width:16, height:2.5,
          background:'rgba(220,38,38,0.55)', borderRadius:2,
          animation:`dp-speed-line 0.22s ease ${i*0.07}s infinite`,
        }}/>
      ))}
      {phase === 'sprint' && [0,1,2].map(i => (
        <div key={i} style={{
          position:'absolute', bottom:12+i*6, left:8+i*6, fontSize: i===2 ? '0.7rem' : '0.9rem',
          animation:`dp-fire-${Math.min(i,1)} ${0.35+i*0.08}s ease-in-out ${i*0.1}s infinite`, zIndex:i+1,
        }}>{i===2?'💥':'🔥'}</div>
      ))}
      {phase === 'jog' && [0,1].map(i => (
        <div key={i} style={{
          position:'absolute', bottom:12, left:8+i*12, width:10, height:10,
          background:'rgba(37,99,235,0.18)', borderRadius:'50%',
          animation:`dp-dust 0.75s ease ${i*0.22}s infinite`,
        }}/>
      ))}
      {phase === 'jog' && (
        <div style={{
          position:'absolute', top:6, right:5, width:6, height:9, background:'rgba(147,197,253,0.9)',
          borderRadius:'50% 50% 50% 50% / 40% 40% 60% 60%', animation:`dp-sweat 0.85s ease infinite`,
        }}/>
      )}
      {phase === 'sprint' && (
        <div style={{ position:'absolute', top:2, right:0, fontSize:'0.75rem', animation:'dp-walk-body 0.3s ease-in-out infinite' }}>⚡</div>
      )}

      <div style={{ animation:`${bodyAnim} ${cfg.bodySpd} ease-in-out infinite` }}>
        <div style={{ width:22, height:22, borderRadius:'50%', background:cfg.skin, margin:'0 auto 2px', position:'relative', border:`2px solid #D97706` }}>
          <div style={{ position:'absolute', top:-2, left:2, width:18, height:8, background:cfg.hair, borderRadius:'8px 8px 0 0' }}/>
          <div style={{ position:'absolute', top:8, left:4,  width:4, height:4, borderRadius:'50%', background:'#1E293B' }}/>
          <div style={{ position:'absolute', top:8, right:4, width:4, height:4, borderRadius:'50%', background:'#1E293B' }}/>
          {phase === 'sprint'
            ? <div style={{ position:'absolute', bottom:4, left:'50%', transform:'translateX(-50%)', width:8, height:2, background:'#7F1D1D', borderRadius:2 }}/>
            : phase === 'jog'
              ? <div style={{ position:'absolute', bottom:4, left:'50%', transform:'translateX(-50%)', width:7, height:3, borderRadius:'0 0 4px 4px', background:'#2563EB' }}/>
              : <div style={{ position:'absolute', bottom:5, left:'50%', transform:'translateX(-50%)', width:5, height:2, background:'#94A3B8', borderRadius:2 }}/>
          }
        </div>

        <div style={{ width:26, height:22, background:cfg.shirt, borderRadius:'6px 6px 3px 3px', margin:'0 auto', position:'relative', border:`2px solid ${cfg.shirt}` }}>
          <div style={{
            position:'absolute', top:4, left:-10, width:10, height:5, background:cfg.skin, borderRadius:4,
            transformOrigin:'right center', animation:`${armL} ${cfg.armSpd} ease-in-out infinite`,
          }}/>
          <div style={{
            position:'absolute', top:4, right:-10, width:10, height:5, background:cfg.skin, borderRadius:4,
            transformOrigin:'left center', animation:`${armR} ${cfg.armSpd} ease-in-out infinite`,
          }}/>
        </div>

        <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
          <div style={{ transformOrigin:'top center', animation:`${legTypeL} ${cfg.legSpd} ease-in-out infinite` }}>
            <div style={{ width:7, height:16, background:cfg.pants, borderRadius:'4px 4px 0 0' }}/>
            <div style={{ width:12, height:5, background:cfg.shoes, borderRadius:'0 5px 5px 2px', marginLeft:2 }}/>
          </div>
          <div style={{ transformOrigin:'top center', animation:`${legTypeR} ${cfg.legSpd} ease-in-out infinite` }}>
            <div style={{ width:7, height:16, background:cfg.pants, borderRadius:'4px 4px 0 0' }}/>
            <div style={{ width:12, height:5, background:cfg.shoes, borderRadius:'0 5px 5px 2px', marginLeft:2 }}/>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   GOAL TRACKER
══════════════════════════════════════════════════════════════ */
function GoalTracker({ sonNet, hedefNet }) {
  const [animPct, setAnimPct] = useState(0);
  const rawPct = hedefNet > 0 ? Math.min((sonNet / hedefNet) * 100, 100) : 0;
  const hedefLabel = hedefNet > 0 ? hedefNet.toFixed(1) : '—';

  useEffect(() => { const t = setTimeout(() => setAnimPct(rawPct), 500); return () => clearTimeout(t); }, [rawPct]);

  const phase = rawPct >= 100 ? 'celebrate' : rawPct >= 70 ? 'sprint' : rawPct >= 30 ? 'jog' : 'walk';

  const phaseMap = {
    walk:      { emoji:'🌱', label:'Yola çıkıyorsun!',     sub:'İlk adım en zor olanıdır — başladın! 💪', primary:'#2563EB', bg:'#EFF6FF', trackBg:'#DBEAFE', fill:'#60A5FA' },
    jog:       { emoji:'⚡', label:'Motor ısındı!',          sub:'Hız kazanıyorsun, ritim tuttu! 🔵',        primary:'#1D4ED8', bg:'#EFF6FF', trackBg:'#BFDBFE', fill:'#2563EB' },
    sprint:    { emoji:'🔥', label:'Hedefe az kaldı!',      sub:'Son sürat tam gaz — bitir şunu! 🔴',       primary:'#DC2626', bg:'#FFF1F2', trackBg:'#FFE4E6', fill:'#E8334A' },
    celebrate: { emoji:'🏆', label:'HEDEFİ GEÇTİN!',      sub:'Muhteşem! Sıradaki hedefe hazırlan! 🎊',   primary:'#DC2626', bg:'#FFF1F2', trackBg:'#FFE4E6', fill:'#E8334A' },
  }[phase];

  const runnerLeft = `calc(${Math.max(2, Math.min(animPct, 87))}% - 27px)`;

  return (
    <div style={{
      background: phaseMap.bg, border: `2px solid ${phaseMap.primary}25`, borderRadius: 22,
      padding: '24px 28px', position: 'relative', overflow: 'hidden',
      transition: 'background 1s ease, border-color 1s ease', boxShadow: `0 4px 24px ${phaseMap.primary}15`,
      animation: phase === 'sprint' ? 'dp-bg-pulse-red 1s ease-in-out infinite' : phase === 'jog' ? 'dp-bg-pulse-blue 1.5s ease-in-out infinite' : undefined,
    }}> 
      <div style={{ position:'absolute', top:-60, right:-60, width:220, height:220, borderRadius:'50%', background:`${phaseMap.primary}08`, pointerEvents:'none' }}/>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:22, position:'relative', zIndex:1 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <span style={{ fontSize:'1.4rem' }}>{phaseMap.emoji}</span>
            <span style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.15rem', color:phaseMap.primary }}>{phaseMap.label}</span>
          </div>
          <div style={{ fontSize:'0.82rem', color:'#64748B', fontWeight:500 }}>{phaseMap.sub}</div>
        </div>
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'2.2rem', lineHeight:1, color: phaseMap.primary, transition:'color 0.8s ease' }}>
            {sonNet.toFixed(1)}
            <span style={{ fontSize:'2.2rem', color:phaseMap.primary, fontWeight:900, fontFamily:'var(--font-ui)' }}>/{hedefLabel}</span>
          </div>
          <div style={{ fontSize:'0.72rem', color:'#94A3B8', fontWeight:600, marginTop:2 }}>net puan</div>
        </div>
      </div>

      <div style={{
        position:'relative', height:88, background:'rgba(255,255,255,0.75)', borderRadius:18,
        border:`1.5px solid ${phaseMap.primary}18`, overflow:'hidden', marginBottom:16, boxShadow:`inset 0 2px 8px ${phaseMap.primary}08`,
      }}>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:10, background:phaseMap.trackBg, borderRadius:'0 0 18px 18px' }}/>
        <div style={{ position:'absolute', bottom:10, left:0, right:0, height:1, background:`${phaseMap.primary}15` }}/>
        <div style={{ position:'absolute', bottom:22, left:0, right:0, height:1, borderTop:`2px dashed ${phaseMap.primary}10` }}/>

        {[25,50,75].map(p => (
          <div key={p} style={{ position:'absolute', bottom:10, left:`${p}%`, transform:'translateX(-50%)' }}>
            <div style={{ width:2, height:12, background:`${phaseMap.primary}30`, margin:'0 auto' }}/>
            <div style={{ fontSize:'0.58rem', fontWeight:800, color:`${phaseMap.primary}80`, marginTop:2, whiteSpace:'nowrap', transform:'translateX(-50%)', position:'relative', left:'50%' }}>{p}%</div>
          </div>
        ))}

        <div style={{
          position:'absolute', bottom:0, left:0, height:10, width:`${animPct}%`,
          background:`linear-gradient(90deg, ${phaseMap.fill}60, ${phaseMap.fill})`, borderRadius:'0 6px 6px 0',
          transition:'width 1.8s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: phase === 'sprint' ? `0 0 20px ${phaseMap.fill}, 0 0 40px ${phaseMap.fill}60` : `0 0 10px ${phaseMap.fill}50`,
        }}/>

        <div style={{ position:'absolute', bottom:10, left:12, display:'flex', flexDirection:'column', alignItems:'center' }}>
          <div style={{ width:3, height:26, background:'#94A3B8', borderRadius:2 }}>
            <div style={{ position:'absolute', top:0, left:3, width:14, height:9, background:'#CBD5E1', clipPath:'polygon(0 0,100% 50%,0 100%)', animation:'dp-flag-wave 2.5s ease-in-out infinite' }}/>
          </div>
        </div>

        <div style={{ position:'absolute', bottom:10, right:12, display:'flex', flexDirection:'column', alignItems:'center' }}>
          <div style={{ fontSize:'0.6rem', fontWeight:800, color:phaseMap.primary, marginBottom:2 }}>HEDEF</div>
          <div style={{ width:3, height:30, background:phaseMap.primary, borderRadius:2, position:'relative', boxShadow: rawPct >= 70 ? `0 0 10px ${phaseMap.primary}` : 'none', transition:'box-shadow 0.6s ease' }}>
            <div style={{
              position:'absolute', top:0, left:3, width:16, height:11, background:phaseMap.primary, clipPath:'polygon(0 0,100% 50%,0 100%)',
              animation: rawPct >= 70 ? `dp-flag-wave ${rawPct >= 100 ? 0.25 : 0.5}s ease-in-out infinite` : 'dp-flag-wave 1.8s ease-in-out infinite',
            }}/>
          </div>
        </div>

        <div style={{ position:'absolute', bottom:10, left: runnerLeft, transition:'left 1.8s cubic-bezier(0.4,0,0.2,1)', display:'flex', flexDirection:'column', alignItems:'center', zIndex:3 }}>
          <RunnerChar phase={phase} />
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', zIndex:1 }}>
        <div style={{ fontSize:'0.8rem', color:'#64748B' }}>
          {hedefNet > 0 && hedefNet - sonNet > 0
            ? <><span style={{ fontWeight:800, color:phaseMap.primary }}>{(hedefNet - sonNet).toFixed(1)} net</span> daha kazanman gerekiyor</>
            : hedefNet > 0
              ? <span style={{ fontWeight:800, color:'#10B981' }}>🎊 Tebrikler, hedefi aştın!</span>
              : <span style={{ fontWeight:800, color:phaseMap.primary }}>Hedef netin tanımlandığında ilerlemeni burada göreceksin.</span>
          }
        </div>
        <div style={{
          display:'flex', alignItems:'center', gap:8, background:'white', border:`2px solid ${phaseMap.primary}30`,
          borderRadius:24, padding:'5px 14px', boxShadow:`0 2px 12px ${phaseMap.primary}15`,
        }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:phaseMap.primary, animation: rawPct >= 70 ? 'dp-dust 0.6s ease infinite' : undefined }}/>
          <span style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.1rem', color:phaseMap.primary }}>%{animPct.toFixed(0)}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   RING PROGRESS
══════════════════════════════════════════════════════════════ */
function RingProgress({ pct, color, size=120, stroke=10, label, value }) {
  const [anim, setAnim] = useState(0);
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  useEffect(() => { setTimeout(() => setAnim(pct), 400); }, [pct]);
  const offset = circ - (anim / 100) * circ;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
      <div style={{ position:'relative', width:size, height:size }}>
        <svg width={size} height={size} style={{ transform:'rotate(-90deg)' }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EEF2FF" strokeWidth={stroke}/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
            style={{ transition:'stroke-dashoffset 1.5s cubic-bezier(0.4,0,0.2,1)', filter:`drop-shadow(0 0 5px ${color}50)` }}
          />
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:size>100?'1.3rem':'1rem', color, lineHeight:1 }}>{value}</div>
          <div style={{ fontSize:'0.6rem', color:'var(--text-muted)', marginTop:2, fontWeight:700 }}>%{parseFloat(pct).toFixed(0)}</div>
        </div>
      </div>
      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:700, textAlign:'center' }}>{label}</div>
    </div>
  );
}

/* Counter */
function Counter({ value, duration=1200 }) {
  const [d, setD] = useState(0);
  useEffect(() => {
    const end = parseFloat(value) || 0;
    if (!end) return;
    const start = Date.now();
    const tick = () => {
      const t = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setD(parseFloat((ease * end).toFixed(1)));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{d}</>;
}

/* Rank row */
function RankRow({ item, isMe, index }) {
  const medals = ['🥇','🥈','🥉'];
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), index*65+150); }, [index]);
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:'var(--r-md)',
      background: isMe ? 'rgba(232,51,74,0.05)' : 'white', border:`1.5px solid ${isMe ? 'rgba(232,51,74,0.28)' : 'var(--border)'}`,
      boxShadow: isMe ? '0 2px 12px rgba(232,51,74,0.1)' : 'var(--shadow-card)',
      opacity: vis?1:0, transform: vis?'none':'translateY(8px)', transition:'all 0.3s ease',
    }}>
      <div style={{ width:28, textAlign:'center', fontSize:'1rem', flexShrink:0 }}>
        {index<3 ? medals[index] : <span style={{ fontSize:'0.8rem', fontWeight:800, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>#{item.siralama||index+1}</span>}
      </div>
      <div style={{ flex:1 }}>
        <span style={{ fontSize:'0.875rem', fontWeight: isMe?800:600, color: isMe?'var(--accent)':'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
          {item.ad} {item.soyad}
        </span>
        {isMe && <span style={{ marginLeft:6, fontSize:'0.65rem', background:'var(--accent)', color:'white', padding:'1px 7px', borderRadius:'var(--r-full)', fontWeight:800 }}>SEN</span>}
      </div>
      <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.1rem', color: isMe?'var(--accent)':'var(--text-secondary)' }}>
        {parseFloat(item.toplam_net||0).toFixed(1)}
      </div>
    </div>
  );
}

/* Badge chip */
function BadgeChip({ rozet, delay=0 }) {
  const [in_, setIn] = useState(false);
  useEffect(() => { setTimeout(() => setIn(true), delay); }, [delay]);
  const icon  = rozet.rozet_adi?.split(' ')[0] || '🏆';
  const cmap  = { '🥇':'#F59E0B','🥈':'#94A3B8','🥉':'#D97706','⭐':'#E8334A','🏆':'#F59E0B' };
  const color = cmap[icon] || '#2563EB';
  return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center', gap:5, padding:'12px 8px',
      background: in_ ? `${color}10` : '#F8FAFF', border:`1.5px solid ${in_ ? color+'40' : 'var(--border)'}`,
      borderRadius:'var(--r-md)', opacity: in_?1:0, transform: in_?'scale(1)':'scale(0.6)',
      transition:'all 0.45s cubic-bezier(0.34,1.56,0.64,1)', boxShadow: in_ ? `0 2px 10px ${color}20` : 'none', cursor:'default',
    }}>
      <div style={{ fontSize:'1.6rem' }}>{icon}</div>
      <div style={{ fontSize:'0.63rem', fontWeight:800, color, textAlign:'center', lineHeight:1.25 }}>
        {rozet.rozet_adi?.split(' ').slice(1).join(' ') || rozet.rozet_adi}
      </div>
    </div>
  );
}

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginBottom:4, fontWeight:700 }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ fontSize:'0.82rem', color:p.color, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:p.color }}/>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN DASHBOARD (BUG GİDERİLDİ!)
══════════════════════════════════════════════════════════════ */
export default function OgrenciDashboard() {
  // `user` verisi AuthContext'ten gelir ve SİSTEME GİREN KİŞİNİN gerçeğidir (Örn: Can Doğan).
  const { user } = useAuth(); 
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [ogrenci, setOgrenci]   = useState(null);
  const [sonuclar, setSonuclar] = useState(null);
  const [basariAnaliz, setBasariAnaliz] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Eğer user henüz yüklenmediyse bekle
    if (!user || !user.id) return;

    (async () => {
      try {
        const listRes = await axios.get('/ogrenciler');
        
        // 🛑 BÜYÜK BUG BURADAYDI! 
        // Eskiden (listRes.data.data || [])[0] diyerek hep "a" kullanıcısını çekiyordu.
        // Şimdi, giriş yapan user.id ile eşleşen KENDİ öğrenci kaydını bulmasını sağladık:
        const list = listRes.data.data || [];
        const me = list.find(s => String(s.user_id ?? s.userId ?? s.kullanici_id ?? s.uid) === String(user.id))
          || (user?.ogrenci_id ? list.find(s => String(s.id) === String(user.ogrenci_id)) : null)
          || (list.length === 1 ? list[0] : null)
          || (user?.ogrenci_id ? { id: user.ogrenci_id } : null);
        
        if (!me) {
          setLoading(false);
          return;
        }

        const [detayRes, sonucRes] = await Promise.all([
          axios.get(`/ogrenciler/${me.id}`),
          axios.get(`/denemeler/ogrenci/${me.id}`),
        ]);
        let basariPayload = null;
        try {
          const basariRes = await axios.get(`/ogrenciler/${me.id}/basari-analiz`);
          basariPayload = basariRes?.data?.data || basariRes?.data;
        } catch (err) {
          try {
            const basariResAlt = await axios.get(`/api/ogrenciler/${me.id}/basari-analiz`);
            basariPayload = basariResAlt?.data?.data || basariResAlt?.data;
          } catch (e2) {}
        }
        if (detayRes.data.success) setOgrenci(detayRes.data.data);
        if (sonucRes.data.success) setSonuclar(sonucRes.data.data);
        if (basariPayload) setBasariAnaliz(basariPayload);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (loading) return (
    <div className="page-loader">
      <div style={{ textAlign:'center' }}>
        <div className="spinner" style={{ width:36, height:36, margin:'0 auto 12px' }}/>
        <p style={{ color:'var(--text-muted)', fontSize:'0.875rem', fontWeight:600 }}>Yükleniyor...</p>
      </div>
    </div>
  );

  const hedefAnaliz = basariAnaliz?.hedef_analiz || null;
  const hedefNet  = parseFloat(hedefAnaliz?.hedef_net || ogrenci?.hedef_net || user?.hedef_net || 0);
  const sonucList = Array.isArray(sonuclar)
    ? sonuclar
    : (sonuclar?.sonuclar?.length
      ? sonuclar.sonuclar
      : (ogrenci?.son_denemeler || []));
  const sonSonuc  = sonucList[sonucList.length - 1];
  const grafikData = basariAnaliz?.grafik_data || [];
  const grafikSon = grafikData.length ? grafikData[grafikData.length - 1] : null;
  const fallbackSonNet = parseFloat(
    grafikSon?.son_net ??
    grafikSon?.toplam_net ??
    sonSonuc?.toplam_net ??
    sonSonuc?.toplamNet ??
    0
  );
  const sonNet    = parseFloat(hedefAnaliz?.son_net ?? fallbackSonNet ?? 0);
  const hedefPct  = hedefNet > 0 ? Math.min((sonNet / hedefNet) * 100, 100) : 0;
  const devData   = ogrenci?.devamsizlik || {};
  const katPct    = devData.toplam_ders > 0 ? ((devData.mevcut||0) / devData.toplam_ders) * 100 : 100;
  const siralama  = sonuclar?.siralama || [];
  const myRank    = siralama.findIndex(s => s.ogrenci_no === ogrenci?.ogrenci_no);
  const netColor  = hedefPct>=100?'#10B981' : hedefPct>=70?'#E8334A' : hedefPct>=30?'#2563EB' : '#94A3B8';
  const devColor  = katPct>80?'#10B981' : katPct>60?'#F59E0B' : '#E8334A';

  const chartData = sonucList.map(s => ({
    name: s.tarih ? new Date(s.tarih).toLocaleDateString('tr-TR',{month:'short',day:'numeric'}) : '-',
    Net:    parseFloat(s.toplam_net||0),
    Hedef:  hedefNet,
  }));

  const odevPct = Math.min(
    ((ogrenci?.odev_istatistik?.teslim_edildi||0) / Math.max(ogrenci?.odev_istatistik?.toplam||1,1)) * 100, 100
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>

      <div style={{
        background:'linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)',
        borderRadius:22, padding:isMobile ? '20px 18px' : '26px 32px', position:'relative', overflow:'hidden', color:'white',
        boxShadow:'0 6px 28px rgba(37,99,235,0.35)', animation:'fadeInDown 0.5s ease both',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:180, height:180, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-30, right:120, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', justifyContent:'space-between', alignItems:isMobile ? 'flex-start' : 'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.6)', letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>
              Öğrenci Paneli
            </div>
            
            {/* 👇 İSİM ARTIK GÜVENLİ BİR ŞEKİLDE CONTEXT'TEN GELİYOR 👇 */}
            <h1 style={{ fontSize:isMobile ? '1.4rem' : '1.8rem', marginBottom:8, color:'white', fontFamily:'var(--font-ui)' }}>
              Merhaba, {user?.ad}! 👋
            </h1>

            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {ogrenci?.sinif_adi && (
                <span style={{ background:'rgba(255,255,255,0.2)', color:'white', padding:'3px 12px', borderRadius:'var(--r-full)', fontSize:'0.75rem', fontWeight:700 }}>
                  {ogrenci.sinif_adi}
                </span>
              )}
              {ogrenci?.sinav_turu && (
                <span style={{ background:'rgba(255,255,255,0.15)', color:'white', padding:'3px 12px', borderRadius:'var(--r-full)', fontSize:'0.75rem', fontWeight:700 }}>
                  {ogrenci.sinav_turu}
                </span>
              )}
            </div>
          </div>
          {myRank >= 0 && myRank < 3 && (
            <div style={{ textAlign:'center', animation:'bounce-in 0.7s ease both 0.6s' }}>
              <div style={{ fontSize:isMobile ? '3rem' : '3.8rem' }}>{['🥇','🥈','🥉'][myRank]}</div>
              <div style={{ fontSize:'0.82rem', color:'rgba(255,255,255,0.9)', fontWeight:900, fontFamily:'var(--font-ui)' }}>{myRank+1}. SIRA</div>
            </div>
          )}
          {sonNet > 0 && (
            <div style={{ background:'rgba(255,255,255,0.18)', borderRadius:18, padding:isMobile ? '10px 14px' : '12px 20px', textAlign:'center', backdropFilter:'blur(4px)', width:isMobile ? '100%' : 'auto' }}>
              <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:isMobile ? '1.6rem' : '2rem', lineHeight:1 }}>
                <Counter value={sonNet} duration={1500}/>
              </div>
              <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.75)', marginTop:2, fontWeight:600 }}>SON NET</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ animation:'fadeInUp 0.5s ease 0.1s both' }}>
        <GoalTracker sonNet={sonNet} hedefNet={hedefNet}/>
      </div>

      <div className="card" style={{ animation:'fadeInUp 0.5s ease 0.2s both' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22, flexWrap:'wrap', gap:10 }}>
          <div>
            <h3 style={{ fontFamily:'var(--font-ui)' }}>Performans Özeti</h3>
            <p style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Genel durum analizi</p>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'space-around', gap:16, flexWrap:'wrap', padding:'4px 0' }}>
          <RingProgress pct={hedefPct}  color={netColor} size={128} stroke={11} label="Hedefe İlerleme" value={sonNet>0?sonNet.toFixed(1):'—'}/>
          <RingProgress pct={katPct}    color={devColor} size={128} stroke={11} label="Devam Oranı"     value={`%${katPct.toFixed(0)}`}/>
          <RingProgress pct={odevPct}   color="#2563EB"  size={128} stroke={11} label="Ödev Teslim"
            value={`${ogrenci?.odev_istatistik?.teslim_edildi||0}/${ogrenci?.odev_istatistik?.toplam||0}`}/>
        </div>
      </div>

      <div className="grid grid-2" style={{ gap:20, gridTemplateColumns: isMobile ? '1fr' : undefined }}>
        <div className="card" style={{ animation:'fadeInUp 0.5s ease 0.3s both' }}>
          <div style={{ marginBottom:14 }}>
            <h3 style={{ fontFamily:'var(--font-ui)', fontSize:'1rem' }}>Net Trendi</h3>
            <p style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
              Hedef: <strong style={{ color:'var(--blue)' }}>{hedefNet > 0 ? `${hedefNet.toFixed(1)} net` : 'Tanımlı değil'}</strong>
            </p>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={188}>
              <AreaChart data={chartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <defs>
                  <linearGradient id="netGL" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={netColor} stopOpacity={0.22}/>
                    <stop offset="95%" stopColor={netColor} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,30,61,0.05)"/>
                <XAxis dataKey="name" tick={{ fill:'var(--text-muted)', fontSize:10, fontWeight:600 }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fill:'var(--text-muted)', fontSize:10, fontWeight:600 }} axisLine={false} tickLine={false}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="Net"   stroke={netColor} strokeWidth={2.5} fill="url(#netGL)" dot={{ fill:netColor, r:3, strokeWidth:0 }} activeDot={{ r:5 }}/>
                <Area type="monotone" dataKey="Hedef" stroke="rgba(37,99,235,0.3)" strokeWidth={1.5} strokeDasharray="5 5" fill="none"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:188, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:'0.875rem' }}>
              Henüz deneme sonucu yok
            </div>
          )}
        </div>

        <div className="card" style={{ animation:'fadeInUp 0.5s ease 0.38s both' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <h3 style={{ fontFamily:'var(--font-ui)', fontSize:'1rem' }}>Sıralama</h3>
              <p style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>Son deneme</p>
            </div>
            {myRank >= 0 && (
              <div style={{ background:'var(--accent)', color:'white', fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'0.9rem', padding:'4px 14px', borderRadius:'var(--r-full)' }}>
                #{myRank+1}
              </div>
            )}
          </div>
          {siralama.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:'0.875rem' }}>
              Sıralama verisi yok
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
              {siralama.slice(0,6).map((item,i) => (
                <RankRow key={i} item={item} isMe={i===myRank} index={i}/>
              ))}
            </div>
          )}
        </div>
      </div>

      {ogrenci?.rozetler?.length > 0 && (
        <div className="card" style={{ animation:'fadeInUp 0.5s ease 0.46s both' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <h3 style={{ fontFamily:'var(--font-ui)', fontSize:'1rem' }}>Rozetlerim 🏅</h3>
              <p style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{ogrenci.rozetler.length} rozet kazandın</p>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(80px, 1fr))', gap:10 }}>
            {ogrenci.rozetler.map((r,i) => <BadgeChip key={i} rozet={r} delay={i*70}/>)}
          </div>
        </div>
      )}
    </div>
  );
}
